/**
 * CrosshairTooltip — the readout that answers "why this bar".
 *
 * One tooltip lists every visible series at the hovered date, so the pointer
 * never has to land on a line to get a value. Values lead and labels follow:
 * the number is the high-contrast element, the series name is secondary.
 * Series are keyed with a short stroke of their colour rather than a filled
 * box — at this density a box is data-weight ink doing a label's job.
 */
import { formatValue } from '../../lib/signalSeries';

function Row({ series, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="flex min-w-0 items-center gap-1.5">
        <span
          className="inline-block h-0.5 w-3 shrink-0 rounded"
          style={{
            background: series.color,
            opacity: series.declared === false ? 0.55 : 1,
          }}
        />
        <span className="truncate text-[10px] text-muted-foreground">
          {series.label}
        </span>
      </span>
      <span className="shrink-0 font-mono text-[11px] font-semibold text-foreground">
        {formatValue(series, value)}
      </span>
    </div>
  );
}

export default function CrosshairTooltip({
  active,
  label,
  payload,
  series = [],
  visible = {},
  evaluation,
  blockers,
}) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload || {};
  const shown = series.filter((s) => visible[s.id] !== false);
  const annotations = shown.filter(
    (s) => s.pane === 'event' || s.pane === 'state'
  );

  return (
    <div className="min-w-[232px] max-w-[300px] rounded-lg border border-border bg-background p-3 shadow-lg">
      <div className="mb-2 flex items-center justify-between gap-2 border-b border-border/60 pb-2">
        <span className="font-mono text-xs font-semibold text-foreground">
          {label}
        </span>
        {evaluation?.passed === true && (
          <span className="rounded bg-win/15 px-1.5 py-0.5 text-[10px] font-bold text-win">
            MATCH
          </span>
        )}
        {evaluation?.passed === false && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
            no match
          </span>
        )}
        {evaluation?.error && (
          <span className="rounded bg-loss/15 px-1.5 py-0.5 text-[10px] font-bold text-loss">
            error
          </span>
        )}
      </div>

      {row.open != null && (
        <div className="mb-2 grid grid-cols-4 gap-x-2 font-mono text-[10px]">
          {[['O', row.open], ['H', row.high], ['L', row.low], ['C', row.close]].map(
            ([key, value]) => (
              <span key={key} className="flex flex-col">
                <span className="text-muted-foreground">{key}</span>
                <span className="font-semibold text-foreground">
                  {value == null ? '—' : value.toFixed(2)}
                </span>
              </span>
            )
          )}
        </div>
      )}

      <div className="space-y-1">
        {shown
          .filter((s) => s.pane !== 'event' && s.pane !== 'state')
          .map((s) => (
            <Row key={s.id} series={s} value={row[s.id]} />
          ))}
      </div>

      {annotations.length > 0 && (
        <div className="mt-2 space-y-1 border-t border-border/60 pt-2">
          {annotations.map((s) => (
            <Row key={s.id} series={s} value={row[s.id]} />
          ))}
        </div>
      )}

      {evaluation?.conditions?.length > 0 && (
        <div className="mt-2 space-y-1 border-t border-border/60 pt-2">
          {evaluation.conditions.map((condition) => (
            <div
              key={condition.key}
              className="flex items-baseline justify-between gap-3"
            >
              <span className="truncate text-[10px] text-muted-foreground">
                {condition.label}
              </span>
              <span
                className={`shrink-0 text-[11px] font-bold ${
                  condition.passed ? 'text-win' : 'text-loss'
                }`}
              >
                {condition.passed ? '✓' : '✗'}
              </span>
            </div>
          ))}
        </div>
      )}

      {blockers?.length > 0 && (
        <p className="mt-2 border-t border-border/60 pt-2 text-[10px] leading-snug text-muted-foreground">
          Not evaluated by the engine on this bar —{' '}
          <span className="font-mono">{blockers.join(', ')}</span> not ready.
        </p>
      )}
    </div>
  );
}
